import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatPrice, COUNTRY_FLAGS, COUNTRY_LABELS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DETAIL } from "@/constants/testIds";
import { MapPin, Bed, Bath, Maximize, Calendar, BadgeCheck, Sparkles, MessageCircle, Heart, Building2, ArrowLeft, Flag } from "lucide-react";
import { MapView } from "@/components/MapView";
import { SEO } from "@/components/SEO";
import { ReportListingModal } from "@/components/ReportListingModal";
import { toast } from "sonner";

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);
  const [message, setMessage] = useState("Bonjour, je suis intéressé(e) par votre annonce. Pouvez-vous me contacter ?");
  const [sending, setSending] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const viewTracked = useRef(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/listings/${id}`);
      setListing(data);
      if (!viewTracked.current) {
        viewTracked.current = true;
        api.post(`/listings/${id}/view`).catch(() => {});
      }
    })();
  }, [id]);

  if (!listing) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-20 text-center text-muted-foreground">Chargement...</div>
      </Layout>
    );
  }

  const send = async () => {
    if (!user) {
      toast.error("Connectez-vous pour contacter l'annonceur");
      return;
    }
    setSending(true);
    try {
      await api.post("/messages", { listing_id: listing.id, body: message });
      toast.success("Message envoyé à l'annonceur");
      setMessage("");
    } catch {
      toast.error("Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  const toggleFav = async () => {
    if (!user) {
      toast.error("Connectez-vous d'abord");
      return;
    }
    try {
      if (favorited) {
        await api.delete(`/favorites/${listing.id}`);
        setFavorited(false);
      } else {
        await api.post(`/favorites/${listing.id}`);
        setFavorited(true);
        toast.success("Ajouté aux favoris");
      }
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <Layout>
      <SEO
        path={`/listings/${listing.id}`}
        title={listing.title}
        description={listing.description?.slice(0, 160)}
        image={listing.photos?.[0]}
        type="article"
        listing={listing}
      />
      <div data-testid={DETAIL.container} className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <Link to="/search" className="inline-flex items-center text-sm text-muted-foreground hover:text-[#D6202C] mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour aux résultats
        </Link>

        {/* Gallery */}
        <div data-testid={DETAIL.gallery} className="grid md:grid-cols-[2fr_1fr] gap-3 mb-8">
          <div className="aspect-[4/3] md:aspect-[16/10] overflow-hidden rounded-2xl bg-[#F5F0EB]">
            <img src={listing.photos[activePhoto]} alt={listing.title} className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-1 gap-3">
            {listing.photos.slice(0, 4).map((p, i) => (
              <button
                key={p}
                onClick={() => setActivePhoto(i)}
                className={`aspect-[4/3] overflow-hidden rounded-2xl border-2 transition ${
                  activePhoto === i ? "border-[#D6202C]" : "border-transparent"
                }`}
              >
                <img src={p} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-[1fr_360px] gap-10">
          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#FEF2F3] text-[#D6202C]">
                {listing.type === "vente" ? "Vente" : "Location"}
              </span>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#11783A]/10 text-[#11783A] uppercase tracking-wider">
                {listing.property_type}
              </span>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-white border border-[#E5E5E5]">
                {COUNTRY_FLAGS[listing.country]} {COUNTRY_LABELS[listing.country]}
              </span>
              {listing.is_boosted && (
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gradient-to-r from-[#F4B400] to-[#D6202C] text-white flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Sponsorisé
                </span>
              )}
            </div>

            <h1 data-testid={DETAIL.title} className="font-heading text-4xl md:text-5xl font-medium tracking-tight leading-tight">
              {listing.title}
            </h1>
            <div className="flex items-center gap-1 mt-3 text-muted-foreground">
              <MapPin className="w-4 h-4" />
              <span>{listing.neighborhood ? `${listing.neighborhood}, ` : ""}{listing.city}</span>
            </div>

            <div data-testid={DETAIL.price} className="mt-6 font-heading text-4xl font-semibold text-[#D6202C]">
              {formatPrice(listing.price, listing.currency, listing.type)}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 my-8">
              <Spec icon={<Maximize />} label="Surface" value={`${Math.round(listing.surface)} m²`} />
              {listing.rooms != null && <Spec icon={<Bed />} label="Pièces" value={listing.rooms} />}
              {listing.bathrooms != null && <Spec icon={<Bath />} label="Salles de bain" value={listing.bathrooms} />}
              {listing.year_built && <Spec icon={<Calendar />} label="Année" value={listing.year_built} />}
            </div>

            <div className="border-t border-[#E5E5E5] pt-8">
              <h2 className="font-heading text-2xl font-medium mb-4">Description</h2>
              <p data-testid={DETAIL.description} className="text-[#5C5C5C] leading-relaxed whitespace-pre-line">
                {listing.description}
              </p>
            </div>

            {listing.amenities?.length > 0 && (
              <div className="border-t border-[#E5E5E5] pt-8 mt-8">
                <h2 className="font-heading text-2xl font-medium mb-4">Équipements</h2>
                <div data-testid={DETAIL.amenities} className="flex flex-wrap gap-2">
                  {listing.amenities.map((a) => (
                    <span key={a} className="px-3 py-1.5 rounded-full bg-[#F5F0EB] text-sm text-[#1A1A1A] border border-[#E5E5E5]">
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {listing.latitude && listing.longitude && (
              <div className="border-t border-[#E5E5E5] pt-8 mt-8">
                <h2 className="font-heading text-2xl font-medium mb-4">Localisation</h2>
                <MapView listings={[listing]} country={listing.country} height="380px" />
              </div>
            )}
          </div>

          {/* Sidebar agent */}
          <div className="md:sticky md:top-24 self-start space-y-4">
            <div data-testid={DETAIL.agentCard} className="bg-white rounded-2xl border border-[#E5E5E5] p-6">
              <Link
                to={`/agence/${listing.owner_id}`}
                data-testid="listing-agency-link"
                className="flex items-center gap-3 mb-4 group"
              >
                <div className="w-14 h-14 rounded-full bg-[#11783A] text-white flex items-center justify-center font-heading text-xl">
                  {listing.owner_name?.charAt(0)}
                </div>
                <div className="flex-1">
                  <div className="font-heading font-medium flex items-center gap-1 group-hover:text-[#D6202C] transition-colors">
                    {listing.owner_name}
                    {listing.owner_badge === "pro_verified" && (
                      <BadgeCheck className="w-4 h-4 text-[#11783A]" />
                    )}
                  </div>
                  {listing.owner_company && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> {listing.owner_company}
                    </div>
                  )}
                  <div className="text-[10px] text-[#D6202C] mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    Voir toutes les annonces →
                  </div>
                </div>
              </Link>

              <Textarea
                data-testid={DETAIL.messageInput}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="rounded-xl mb-3"
              />
              <Button
                onClick={send}
                disabled={sending}
                data-testid={DETAIL.sendMessageBtn}
                className="w-full rounded-xl bg-[#D6202C] hover:bg-[#B5181F] h-11"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {sending ? "Envoi..." : "Envoyer le message"}
              </Button>
              <Button
                onClick={toggleFav}
                variant="outline"
                data-testid={DETAIL.contactBtn}
                className="w-full rounded-xl mt-2 h-11"
              >
                <Heart className={`w-4 h-4 mr-2 ${favorited ? "fill-[#D6202C] text-[#D6202C]" : ""}`} />
                {favorited ? "Dans vos favoris" : "Ajouter aux favoris"}
              </Button>
            </div>

            <div className="bg-[#11783A] text-white rounded-2xl p-6">
              <Sparkles className="w-6 h-6 text-[#F4B400] mb-2" />
              <div className="font-heading font-medium mb-1">Annonce vérifiée</div>
              <div className="text-xs text-white/70">
                Tous nos annonceurs Pro sont vérifiés manuellement pour garantir des transactions sereines.
              </div>
            </div>

            {user && user.id !== listing.owner_id && (
              <button
                onClick={() => setReportOpen(true)}
                data-testid="listing-report-btn"
                className="w-full text-xs text-muted-foreground hover:text-[#D6202C] inline-flex items-center justify-center gap-1.5 py-2 transition-colors"
              >
                <Flag className="w-3 h-3" /> Signaler cette annonce
              </button>
            )}
          </div>
        </div>
      </div>
      <ReportListingModal
        listingId={listing.id}
        listingTitle={listing.title}
        open={reportOpen}
        onClose={() => setReportOpen(false)}
      />
    </Layout>
  );
}

const Spec = ({ icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-[#E5E5E5] p-4">
    <div className="w-9 h-9 rounded-lg bg-[#FEF2F3] text-[#D6202C] flex items-center justify-center mb-2">
      {icon}
    </div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="font-heading font-semibold text-lg mt-0.5">{value}</div>
  </div>
);
